import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { WA_LINK } from "@/data";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = "";
  }, [location]);

  const toggleMenu = () => {
    setIsOpen((v) => {
      document.body.style.overflow = v ? "" : "hidden";
      return !v;
    });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/90 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="navbar-logo"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_wellness-andheri/artifacts/hcc2ecna_change_the_logo_202604131845.png"
            alt="Raj Muscle Care Studio"
            className="h-[60px] sm:h-[110px] w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-[#FF4500]"
                  : "text-gray-300 hover:text-white"
              }`}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:scale-105 min-h-[44px]"
          data-testid="nav-book-whatsapp-btn"
        >
          Book on WhatsApp
        </a>

        {/* Mobile Hamburger */}
        <button
          onClick={toggleMenu}
          className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Toggle menu"
          data-testid="nav-hamburger-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 top-[60px] bg-black/97 backdrop-blur-xl z-40 flex flex-col">
          <div className="flex flex-col gap-2 p-6 pt-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-medium py-3 px-4 rounded-xl transition-colors ${
                  location.pathname === link.path
                    ? "text-[#FF4500] bg-[#FF4500]/10"
                    : "text-gray-300 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#FF4500] text-white font-semibold py-4 rounded-full mt-6 text-base min-h-[56px]"
              data-testid="nav-mobile-book-btn"
            >
              Book on WhatsApp
            </a>
            <a
              href="tel:+919004250500"
              className="flex items-center justify-center gap-2 border border-white/20 text-white font-semibold py-4 rounded-full mt-2 text-base min-h-[56px] hover:bg-white/5 transition-colors"
              data-testid="nav-mobile-call-btn"
            >
              Call Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
